import { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { Link, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { queryClient } from "queryClient";
import ticketsService from "services/admin/ticketsService";
import { useGetTicket } from "hooks/useGetTicket";
import {
  Ticket,
  TicketStatus,
  TicketCloseReason,
  MANUAL_CLOSE_REASONS,
  isTicketTransitionAllowed,
} from "interfaces/entities/ticket.model";
import { useToasts, Toasts, DesignStatus, DesignPriority } from "./v2/primitives";
import { ConversationColumn, ConvTab } from "./v2/ConversationColumn";
import { SidebarColumn } from "./v2/SidebarColumn";
import { priorityFromApp, statusToApp, priorityToApp } from "./v2/mappings";
import "./v2/styles.scss";

/**
 * New two-column ticket workspace (design v2).
 *
 *   /admin/tickets/:id/v2
 *
 * Existing /admin/tickets/:id continues to render the v1 page so the team
 * can compare the two side-by-side.
 *
 * The page reuses the existing tickets service for status / priority /
 * assignee writes, and the existing message + event hooks for reads. SLA,
 * related tickets, and global search are mocked — see TODO notes at each
 * site.
 */
const TicketDetailPageV2 = () => {
  const { id } = useParams<{ id: string }>();
  const { data: ticket, isLoading, error } = useGetTicket(id);

  if (isLoading) {
    return (
      <div style={{ display: "grid", placeItems: "center", padding: 64 }}>
        <CircularProgress size={28} />
      </div>
    );
  }
  if (error || !ticket) {
    return (
      <div style={{ padding: 32 }}>
        <Link to="/admin/tickets" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <FaArrowLeft size={11} /> К списку обращений
        </Link>
        <div style={{ marginTop: 16, color: "#B91C1C" }}>Не удалось загрузить обращение</div>
      </div>
    );
  }

  return <TicketDetailWorkspace ticket={ticket} />;
};

interface WorkspaceProps {
  ticket: Ticket;
}

const TicketDetailWorkspace = ({ ticket }: WorkspaceProps) => {
  const { t } = useTranslation();
  // Local UI state mirrors the design's React `useState` set. Backend writes
  // happen via the existing ticketsService; we keep optimistic UI state in
  // sync so the pickers feel instant.
  const [status, setStatus] = useState<any>(ticket.status);
  const [priority, setPriority] = useState<DesignPriority>(priorityFromApp(ticket.priority));
  const [assigneeID, setAssigneeID] = useState<string | null>(ticket.memberID || null);
  const [type, setType] = useState<string>(ticket.type || "");
  const [categoryID, setCategoryID] = useState<string>(ticket.categoryID || "");
  const [branchID, setBranchID] = useState<string>(ticket.branchID || "");
  const [tags, setTags] = useState<string[]>(() =>
    Array.isArray((ticket as any).tags) ? ((ticket as any).tags as string[]) : [],
  );
  const [tab, setTab] = useState<ConvTab>("conversation");
  const [starred, setStarred] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closeReason, setCloseReason] = useState<TicketCloseReason | "">("");
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);

  // Resync local state only when the underlying ticket *changes* (i.e. the
  // user navigated to a different ticket). Resyncing on every field change
  // caused a race with optimistic updates — the just-picked assignee would
  // briefly snap back to the cached value while the refetch was in flight.
  useEffect(() => {
    setStatus(ticket.status);
    setPriority(priorityFromApp(ticket.priority));
    setAssigneeID(ticket.memberID || null);
    setType(ticket.type || "");
    setCategoryID(ticket.categoryID || "");
    setBranchID(ticket.branchID || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket.id]);

  // Hide the admin shell's top header while this workspace is mounted —
  // the v2 design is meant to be the full-bleed focal surface.
  useEffect(() => {
    document.body.classList.add("hide-admin-header");
    return () => document.body.classList.remove("hide-admin-header");
  }, []);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["tickets"] });
    queryClient.invalidateQueries({ queryKey: ["tickets", ticket.id] });
    queryClient.invalidateQueries({ queryKey: ["events", "ticket", ticket.id] });
  };

  // Status change — ported from TicketForm.onStatusButtonClick so the v2
  // flow matches v1 exactly:
  //   • picking CLOSED  → open close-reason dialog (no direct write; reason
  //                       comes via POST /tickets/:id/close)
  //   • current = CLOSED → open reopen dialog (POST /tickets/:id/reopen)
  //   • picking RESOLVED → POST /tickets/:id/resolve
  //   • anything else    → PATCH /tickets/:id { status } (the backend's
  //                       updateStatus handler validates transitions itself)
  // Transition guard mirrors `isTicketTransitionAllowed` so the user sees a
  // warning before the request fires.
  const handleStatusChange = (target: string) => {
    const current = ticket.status as TicketStatus;
    if (target === current) return;

    if (target === TicketStatus.CLOSED) {
      setCloseReason("");
      setCloseDialogOpen(true);
      return;
    }
    // Переоткрытие — только из CLOSED, и только через отдельную кнопку в заголовке (см. handleMarkResolved), чтобы не мешать обычному workflow изменения статуса.
    if (current === TicketStatus.CLOSED) {
      setReopenDialogOpen(true);
      return;
    }
    if (!isTicketTransitionAllowed(current, target)) {
      toast.warn(
        t("ticket.transitionNotAllowed", {
          from: t(`ticket.status.${current}`),
          to: t(`ticket.status.${target}`),
        }),
      );
      return;
    }

    // Optimistic UI: flip immediately, roll back on error.
    setStatus(target);

    void (async () => {
      try {
        if (target === TicketStatus.RESOLVED) {
          await ticketsService.resolve(ticket.id);
        } else {
          await ticketsService.patch(ticket.id, { status: target } as any);
        }
        invalidate();
        toast.success(t("ticket.statusUpdated"));
      } catch (err: any) {
        console.error(err);
        setStatus(current);
        toast.error(err?.response?.data?.message || t("ticket.statusUpdateError"));
      }
    })();
  };

  const persistPriority = async (next: DesignPriority) => {
    try {
      await ticketsService.put(ticket.id, { priority: priorityToApp(next) } as any);
      invalidate();
      toast.info(`Приоритет: ${next}`);
    } catch (err) {
      console.error(err);
      setPriority(priorityFromApp(ticket.priority));
      toast.error("Не удалось изменить приоритет");
    }
  };

  // Send the full ticket payload via PUT (the same shape the v1 TicketForm
  // uses on save). The backend's `update` controller only applies truthy
  // fields, so sending the rest as-is is a no-op but keeps the field we
  // care about in a single round-trip and avoids any zod-style validation
  // surprises around partial bodies.
  const buildFullPayload = (override: Partial<Ticket>) => ({
    subject: ticket.subject,
    content: ticket.content,
    status: ticket.status,
    priority: ticket.priority,
    type: ticket.type,
    categoryID: ticket.categoryID,
    memberID: ticket.memberID,
    customerID: ticket.customerID,
    branchID: ticket.branchID,
    ...override,
  });

  const persistAssignee = async (next: string | null) => {
    try {
      await ticketsService.put(
        ticket.id,
        buildFullPayload({ memberID: (next || "") as any }) as any,
      );
      invalidate();
      toast.info(next ? "Ответственный назначен" : "Снят с ответственного");
    } catch (err) {
      console.error(err);
      setAssigneeID(ticket.memberID || null);
      toast.error("Не удалось изменить ответственного");
    }
  };

  const persistType = async (next: string) => {
    try {
      await ticketsService.put(ticket.id, buildFullPayload({ type: next }) as any);
      invalidate();
      toast.info("Тип обращения обновлён");
    } catch (err) {
      console.error(err);
      setType(ticket.type || "");
      toast.error("Не удалось изменить тип");
    }
  };

  const persistCategory = async (next: string) => {
    try {
      await ticketsService.put(
        ticket.id,
        buildFullPayload({ categoryID: (next || "") as any }) as any,
      );
      invalidate();
      toast.info("Тематика обновлена");
    } catch (err) {
      console.error(err);
      setCategoryID(ticket.categoryID || "");
      toast.error("Не удалось изменить тематику");
    }
  };

  const persistBranch = async (next: string) => {
    try {
      await ticketsService.put(
        ticket.id,
        buildFullPayload({ branchID: (next || "") as any }) as any,
      );
      invalidate();
      toast.info("Филиал обновлён");
    } catch (err) {
      console.error(err);
      setBranchID(ticket.branchID || "");
      toast.error("Не удалось изменить филиал");
    }
  };

  const handlePriorityChange = (next: DesignPriority) => {
    setPriority(next);
    void persistPriority(next);
  };
  const handleAssigneeChange = (next: string | null) => {
    setAssigneeID(next);
    void persistAssignee(next);
  };
  const handleTypeChange = (next: string) => {
    setType(next);
    void persistType(next);
  };
  const handleCategoryChange = (next: string) => {
    setCategoryID(next);
    void persistCategory(next);
  };
  const handleBranchChange = (next: string) => {
    setBranchID(next);
    void persistBranch(next);
  };

  // TODO(tags): backend currently doesn't accept a `tags` field on PATCH.
  // We keep the list client-side so the design's add/remove flow is
  // reviewable. When the backend lands the tag column, replace these with
  // a service call.
  const handleAddTag = (tag: string) => {
    setTags((prev) => (prev.includes(tag) ? prev : [...prev, tag]));
    toast.info(`Тег «${tag}» добавлен`);
  };
  const handleRemoveTag = (tag: string) => {
    setTags((prev) => prev.filter((t) => t !== tag));
    toast.info(`Тег «${tag}» удалён`);
  };

  // The header "Решено / Переоткрыть" button is just a shortcut into the
  // same handleStatusChange path used by the sidebar status popover.
  //   • CLOSED   → open reopen dialog
  //   • RESOLVED → reopen back to IN_PROGRESS (mirrors v1 chip click)
  //   • otherwise → mark resolved (resolve() endpoint)
  const handleMarkResolved = () => {
    const current = ticket.status as TicketStatus;
    if (current === TicketStatus.CLOSED) {
      setReopenDialogOpen(true);
    } else {
      setCloseDialogOpen(true);
    }
  };

  const confirmClose = async () => {
    if (!closeReason) {
      toast.warn(t("ticket.closeReasonRequired"));
      return;
    }
    try {
      await ticketsService.close(ticket.id, closeReason as TicketCloseReason);
      setStatus("CLOSED");
      setCloseDialogOpen(false);
      setCloseReason("");
      invalidate();
      toast.success(t("ticket.closed"));
    } catch (err: any) {
      console.error(err);
      toast.error(err?.response?.data?.message || t("ticket.closeError"));
    }
  };

  const confirmReopen = async () => {
    try {
      await ticketsService.reopen(ticket.id);
      // Backend returns to IN_PROGRESS on reopen.
      setStatus("IN_PROGRESS");
      setReopenDialogOpen(false);
      invalidate();
      toast.success(t("ticket.reopened"));
    } catch (err: any) {
      console.error(err);
      toast.error(err?.response?.data?.message || t("ticket.reopenError"));
    }
  };

  return (
    <div className="ticket-v2-root">
      <div className="tv2-main">
        <ConversationColumn
          ticket={ticket}
          status={status}
          priority={priority}
          starred={starred}
          onToggleStar={() => {
            setStarred((v) => !v);
            toast.info(starred ? "Снято с отметки" : "Отмечено");
          }}
          onMarkResolved={handleMarkResolved}
          tab={tab}
          onTabChange={setTab}
        />
        <SidebarColumn
          ticket={ticket}
          status={status}
          onStatusChange={handleStatusChange}
          priority={priority}
          onPriorityChange={handlePriorityChange}
          assigneeID={assigneeID}
          onAssigneeChange={handleAssigneeChange}
          type={type}
          onTypeChange={handleTypeChange}
          categoryID={categoryID}
          onCategoryChange={handleCategoryChange}
          branchID={branchID}
          onBranchChange={handleBranchChange}
          tags={tags}
          onAddTag={handleAddTag}
          onRemoveTag={handleRemoveTag}
        />
      </div>

      {/* Close-reason dialog — mirrors the v1 TicketForm flow: "Решено"
          requires a reason before persisting the CLOSED status. */}
      <Dialog
        fullWidth
        maxWidth="sm"
        open={closeDialogOpen}
        onClose={() => setCloseDialogOpen(false)}
      >
        <DialogTitle>{t("ticket.close")}</DialogTitle>
        <DialogContent>
          <div className="mb-4">{t("ticket.closeConfirm")}</div>
          <FormControl fullWidth size="small">
            <InputLabel id="ticket-v2-close-reason-label">
              {t("ticket.closeReasonLabel")}
            </InputLabel>
            <Select
              labelId="ticket-v2-close-reason-label"
              label={t("ticket.closeReasonLabel")}
              value={closeReason}
              onChange={(e) => setCloseReason(e.target.value as TicketCloseReason)}
            >
              {MANUAL_CLOSE_REASONS.map((reason) => (
                <MenuItem key={reason} value={reason}>
                  {t(`ticket.closeReason.${reason}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="info" onClick={() => setCloseDialogOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!closeReason}
            onClick={confirmClose}
          >
            {t("ticket.close")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        fullWidth
        maxWidth="sm"
        open={reopenDialogOpen}
        onClose={() => setReopenDialogOpen(false)}
      >
        <DialogTitle>{t("ticket.reopen")}</DialogTitle>
        <DialogContent>
          <div>{t("ticket.reopenConfirm")}</div>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="info" onClick={() => setReopenDialogOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button variant="contained" color="success" onClick={confirmReopen}>
            {t("ticket.reopen")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TicketDetailPageV2;
